import { useMutation } from '@tanstack/react-query';
import { businessClaimsService } from '../services/business-claims-service';
import { categoryService } from '../services/category-service';
import { resolveBusinessClaimsCategoryIds } from '../services/business-claims-vendor-groups';
import { getCurrentUserId } from '../lib/supabase';
import type {
  ApplyBusinessClaimsTaxInput,
  ScanBusinessClaimsInput,
  VendorBusinessClaimApplyRow,
} from '../types/business-claims.types';
import { useQueryClient } from '@tanstack/react-query';
import { transactionKeys } from './useTransactions';

export function useScanBusinessClaimsVendors() {
  return useMutation({
    mutationFn: async (params: Omit<ScanBusinessClaimsInput, 'userId' | 'categoryIds'>) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const categoriesResult = await categoryService.listCategories({ includeSystem: true });
      if (categoriesResult.error) {
        throw new Error(categoriesResult.error.message);
      }

      const categoryIds = resolveBusinessClaimsCategoryIds(categoriesResult.data?.categories ?? []);
      if (!categoryIds.length) {
        throw new Error(
          'No Business Expenses category found. Add business categories before preparing claims.'
        );
      }

      const result = await businessClaimsService.scanVendorGroups({
        userId,
        categoryIds,
        ...params,
      });

      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data ?? [];
    },
  });
}

export function useApplyVendorBusinessUse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      rows: VendorBusinessClaimApplyRow[];
      overwriteExisting: boolean;
    }) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      const result = await businessClaimsService.applyVendorBusinessUse(
        userId,
        params.rows,
        { overwriteExisting: params.overwriteExisting }
      );
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
    },
  });
}

export function useApplyBusinessClaimsTax() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      params: Omit<ApplyBusinessClaimsTaxInput, 'userId'>
    ) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const result = await businessClaimsService.applyTaxFields({ userId, ...params });
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
    },
  });
}
